import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  useTheme,
  Chip, 
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MyButton } from '../index';
import { rejectReasonModalStyles as styles } from '../RejectReasonModal/RejectReasonModal.styles';
import { format } from 'date-fns';

const ApproveConfirmModal = ({
  open = false,
  onClose,
  onConfirm,
  title,
  message,
  item,
  loading = false,
  confirmText,
  cancelText,
  entityType = 'leave',
  ...props
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const modalTitle = title || t(`${entityType}.actions.approve`);
  const entityLabel =
    entityType === 'expenses'
      ? 'masraf'
      : entityType === 'userRevenueEntries'
        ? 'ciro'
        : 'izin';
  const modalMessage =
    message ||
    t(
      `${entityType}.modals.approve.message`,
      `Bu ${entityLabel} talebini onaylamak istediğinize emin misiniz?`
    );
  const modalConfirmText = confirmText || t(`${entityType}.actions.approve`);
  const modalCancelText = cancelText || t('modals.delete.cancel');

  // Tarih formatlama fonksiyonu
  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return format(new Date(date), 'dd.MM.yyyy HH:mm');
    } catch {
      return date;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose(e, reason);
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      sx={styles.dialog}
      {...props}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Box sx={styles.titleContainer}>
          <Box sx={styles.titleContent}>
            <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 32, mr: 1 }} />
            <Typography variant="h6" sx={styles.titleText}>
              {modalTitle}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={loading}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Typography variant="body1" sx={{ ...styles.messageText, mb: 2 }}>
          {modalMessage}
        </Typography>
        {item && (
          <Box sx={{
            background: theme.palette.background.default,
            borderRadius: 2,
            p: 2.5,
            width: '100%',
            boxShadow: '0 2px 12px 0 rgba(60,60,60,0.07)',
            border: `1px solid ${theme.palette.divider}`,
            mb: 2,
          }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t(`${entityType}.table.user`)}: <span style={{ fontWeight: 400 }}>{item?.user?.first_name || item?.user?.firstName || ''} {item?.user?.last_name || item?.user?.lastName || ''}</span>
            </Typography>
            {entityType === 'leaves' ? (
              <>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('leaves.table.startDate')}: <span style={{ fontWeight: 400 }}>{formatDate(item?.startDate)}</span>
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('leaves.table.endDate')}: <span style={{ fontWeight: 400 }}>{formatDate(item?.endDate)}</span>
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('leaves.table.duration')}: <span style={{ fontWeight: 400 }}>{item?.duration || 0} gün</span>
                </Typography>
                <Box mt={1}>
                  <Chip
                    onClick={() => {
                    }}
                    label={item?.leaveTypeName || '-'}
                    color="info"
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.95em', letterSpacing: 0.2 }}
                  />
                </Box>
              </>
            ) : entityType === 'userRevenueEntries' ? (
              // Ciro talepleri için tutar/açıklama/yıl-çeyrek + ciro kanalı chip'i.
              // user_revenue_entries row'unda expenses field'ları yok; doğru
              // alanlardan okumadıkça modal hep "-/0₺" gösteriyordu.
              <>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('userRevenueEntries.table.tutar', 'Tutar')}:{' '}
                  <span style={{ fontWeight: 400 }}>
                    {Number(item?.tutar || 0).toLocaleString('tr-TR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    ₺
                  </span>
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('userRevenueEntries.table.period', 'Dönem')}:{' '}
                  <span style={{ fontWeight: 400 }}>
                    {item?.period_label || `${item?.yil || '-'}/Q${item?.ceyrek || '-'}`}
                  </span>
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('userRevenueEntries.table.firm', 'Firma')}:{' '}
                  <span style={{ fontWeight: 400 }}>{item?.firm_full_name || item?.firm_name || '-'}</span>
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('userRevenueEntries.table.referans', 'Referans')}:{' '}
                  <span style={{ fontWeight: 400 }}>{item?.referans || '-'}</span>
                </Typography>
                {/* Ay Sayisi (x) — kullanıcı kaydettiği pay. Sadece
                    ONAY_SONRASI icin anlamli; degil ya da bos ise gizle. */}
                {item?.ciro_kanali === 'ONAY_SONRASI_CIROSU'
                 && item?.ay_sayisi != null
                 && item?.ay_sayisi !== '' && (
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {t('userRevenueEntries.form.aySayisi', 'Ay Sayısı (Bu Kayıt)')}:{' '}
                    <span style={{ fontWeight: 400 }}>{item.ay_sayisi} ay</span>
                  </Typography>
                )}
                {/* Prim alinan aylar — ONAY_SONRASI icin kullanicinin sectigi
                    ay numaralarini (1..12) i18n label'lariyla virgullu goster. */}
                {item?.ciro_kanali === 'ONAY_SONRASI_CIROSU'
                 && Array.isArray(item?.secili_aylar)
                 && item.secili_aylar.length > 0 && (
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {t('userRevenueEntries.form.seciliAylar', 'Prim Alınan Aylar')}:{' '}
                    <span style={{ fontWeight: 400 }}>
                      {item.secili_aylar
                        .map((n) => Number(n))
                        .filter((n) => n >= 1 && n <= 12)
                        .sort((a, b) => a - b)
                        .map((n) => t(`userRevenueEntries.form.months.${n}`))
                        .join(', ')}
                    </span>
                  </Typography>
                )}
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('userRevenueEntries.table.aciklama', 'Açıklama')}:{' '}
                  <span style={{ fontWeight: 400 }}>{item?.aciklama || '-'}</span>
                </Typography>
                <Box mt={1}>
                  <Chip
                    label={item?.ciro_kanali_label || item?.ciro_kanali || '-'}
                    color="info"
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.95em', letterSpacing: 0.2 }}
                  />
                </Box>
              </>
            ) : (
              <>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('expenses.table.amount')}: <span style={{ fontWeight: 400 }}>{item?.amount || 0} ₺</span>
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('expenses.table.description')}: <span style={{ fontWeight: 400 }}>{item?.description || '-'}</span>
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('expenses.table.expenseDate')}: <span style={{ fontWeight: 400 }}>{formatDate(item?.expenseDate)}</span>
                </Typography>
                <Box mt={1}>
                  <Chip
                    onClick={() => {
                    }}
                    label={item?.expenseTypeName || '-'}
                    color="info"
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.95em', letterSpacing: 0.2 }}
                  />
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <MyButton
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={styles.cancelButton}
        >
          {modalCancelText}
        </MyButton>
        <MyButton
          variant="contained"
          color="success"
          onClick={onConfirm}
          disabled={loading}
          sx={styles.confirmButton}
        >
          {loading ? t('leaves.modals.approve.approving', 'Onaylanıyor...') : modalConfirmText}
        </MyButton>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveConfirmModal; 